import { getAssetTypeConfig } from '@/shared/api/openmetadata';

/**
 * 자산 타입별 아이콘 스타일 정보
 */
export type AssetIconStyle = {
  /** 아이콘 래퍼 배경색 */
  backgroundColor: string;
  /** 아이콘 래퍼 테두리 색상 */
  borderColor: string;
  /** 아이콘 색상 */
  iconColor: string;
};

/**
 * 자산 타입에 따른 아이콘 스타일을 반환합니다.
 *
 * @description
 * getAssetTypeConfig를 사용하여 자산 타입별로 정의된
 * 배경색, 테두리 색상, 아이콘 색상을 반환합니다.
 * 설정이 없는 경우 기본 회색 스타일을 반환합니다.
 *
 * @param entityType - 자산 타입 (예: 'table', 'database', 'topic')
 * @returns 아이콘 스타일 객체 (backgroundColor, borderColor, iconColor)
 *
 * @example
 * ```tsx
 * const iconStyle = getAssetIconStyle('table');
 * // {
 * //   backgroundColor: 'var(--opds-sem-color-container-colorful-magenta-level1)',
 * //   borderColor: 'var(--opds-sem-color-container-colorful-magenta-level1)',
 * //   iconColor: 'var(--opds-sem-color-onContainer-colorful-magenta)'
 * // }
 *
 * <div style={{ backgroundColor: iconStyle.backgroundColor }}>
 *   <Icon color={iconStyle.iconColor} />
 * </div>
 * ```
 */
export const getAssetIconStyle = (entityType: string): AssetIconStyle => {
  const assetTypeConfig = getAssetTypeConfig(entityType);

  const backgroundColor =
    assetTypeConfig.tagBackgroundColor || 'var(--opds-sem-color-container-standard-opacity-level1)';
  const borderColor = assetTypeConfig.tagBackgroundColor || 'var(--opds-sem-color-container-standard-opacity-level1)';
  const iconColor = assetTypeConfig.color || 'var(--opds-glo-palette-gray-50)';

  return {
    backgroundColor,
    borderColor,
    iconColor,
  };
};

/**
 * 자산 타입에 따른 아이콘 래퍼 인라인 스타일을 반환합니다.
 *
 * @description
 * ContentHeader 등에서 iconWrapperStyle로 직접 사용할 수 있는
 * 인라인 스타일 객체를 반환합니다.
 *
 * @param entityType - 자산 타입
 * @returns React 인라인 스타일 객체
 *
 * @example
 * ```tsx
 * <ContentHeader
 *   icon={<Icon />}
 *   iconWrapperStyle={getAssetIconWrapperStyle('table')}
 * />
 * ```
 */
export const getAssetIconWrapperStyle = (entityType: string): React.CSSProperties => {
  const { backgroundColor, borderColor } = getAssetIconStyle(entityType);

  return {
    backgroundColor,
    border: `1px solid ${borderColor}`,
  };
};
